import { useEffect, useRef, useState } from "react";
import emojiData from "@emoji-mart/data";
import Picker from "@emoji-mart/react";
import { X } from "lucide-react";
import { cn } from "@/lib/utils";

interface EmojiPickerFieldProps {
  value: string;
  onChange: (value: string) => void;
  ariaLabel?: string;
  placeholder?: string;
  className?: string;
}

export function EmojiPickerField({
  value,
  onChange,
  ariaLabel = "Seleccionar emoji",
  placeholder = "Seleccionar emoji",
  className,
}: EmojiPickerFieldProps) {
  const [open, setOpen] = useState(false);
  const pickerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;

    const handlePointerDown = (event: MouseEvent) => {
      if (
        pickerRef.current &&
        !pickerRef.current.contains(event.target as Node)
      ) {
        setOpen(false);
      }
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [open]);

  return (
    <div className={cn("relative", className)} ref={pickerRef}>
      <div className="flex h-10 items-center rounded-md border border-input bg-background focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2">
        <button
          type="button"
          className="flex h-full min-w-0 flex-1 items-center px-3 text-left text-sm focus-visible:outline-none"
          aria-label={ariaLabel}
          aria-expanded={open}
          aria-haspopup="dialog"
          onClick={() => setOpen((current) => !current)}
        >
          <span className={value ? "" : "truncate text-muted-foreground"}>
            {value || placeholder}
          </span>
        </button>
        {value && (
          <button
            type="button"
            className="mr-1 flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            aria-label="Quitar emoji"
            onClick={() => onChange("")}
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>
      {open && (
        <div className="absolute left-0 z-50 mt-1" role="dialog">
          <Picker
            data={emojiData}
            onEmojiSelect={(emoji: { native: string }) => {
              onChange(emoji.native);
              setOpen(false);
            }}
            theme="dark"
            locale="es-MX"
            previewPosition="none"
            skinTonePosition="none"
          />
        </div>
      )}
    </div>
  );
}
